import { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import { PrinterFill } from "react-bootstrap-icons";
import { fetchInfracciones as apiFetchInfracciones, findUserByName } from "../services/api";
import { getStoredUser } from "../utils/session";
import { generarTicket } from "./generarTicket";
import "./misinfracciones.css";

function normalizeText(value) {
    return String(value || "").trim().toLowerCase();
}

export default function MisInfracciones() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [misInfracciones, setMisInfracciones] = useState([]);

    const user = getStoredUser();
    const nombreAgente = user?.nombre || "";

    useEffect(() => {
        let mounted = true;

        async function loadMisInfracciones() {
            setLoading(true);
            setError("");

            try {
                const data = await apiFetchInfracciones({ eliminado: false });
                const filtradas = (data || []).filter(
                    (inf) => normalizeText(inf.agente) === normalizeText(nombreAgente)
                );

                if (mounted) {
                    setMisInfracciones(filtradas);
                }
            } catch (fetchError) {
                if (mounted) {
                    setError("No se pudo cargar la lista de infracciones.");
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        }

        if (nombreAgente) {
            loadMisInfracciones();
        } else {
            setLoading(false);
            setError("No se pudo identificar el agente en sesión.");
        }

        return () => {
            mounted = false;
        };
    }, [nombreAgente]);

    const rows = useMemo(() => {
        const q = normalizeText(query);
        if (!q) {
            return misInfracciones;
        }

        return misInfracciones.filter((inf) => {
            const conductor = `${inf.infractor_nombre || ""} ${inf.infractor_apellido_paterno || ""} ${inf.infractor_apellido_materno || ""}`;
            return (
                normalizeText(inf.id).includes(q) ||
                normalizeText(conductor).includes(q) ||
                normalizeText(inf.fecha).includes(q)
            );
        });
    }, [misInfracciones, query]);

    async function handleReimprimir(inf) {
        let agenteData = null;
        try {
            agenteData = await findUserByName(inf.agente);
        } catch (findError) {
            // Si no se encuentra nomina, de todas formas se permite la reimpresion.
        }

        const form = {
            nombre: inf.infractor_nombre,
            apellidoPaterno: inf.infractor_apellido_paterno,
            apellidoMaterno: inf.infractor_apellido_materno,
            marca: inf.vehiculo_marca,
            submarca: inf.vehiculo_submarca,
            modelo: inf.vehiculo_modelo,
            color: inf.vehiculo_color,
            serieAuto: inf.vehiculo_serie_auto,
            placas: inf.vehiculo_placas,
            motivo: inf.motivo,
            articulos: inf.articulos,
            cantidadUMAs: inf.cantidadUMAs,
            seRetuvo: inf.se_retuvo,
            calle: inf.calle,
            entreCalle: inf.entre_calle,
            yCalle: inf.y_calle,
            municipio: inf.municipio,
            colonia: inf.colonia,
            folioInventario: inf.vehiculo_folio_inventario,
            deposito: inf.vehiculo_deposito,
            nombreAgente: agenteData?.nombre || inf.agente || nombreAgente,
            nominaAgente: agenteData?.nomina || "",
            serieEquipo: "20252027",
            fecha: inf.fecha,
            hora: inf.hora,
        };

        const ticketFolio = inf?.id || inf?.folio || inf?.infraccion?.id || inf?.infraccion_id;
        await generarTicket(form, ticketFolio);
    }

    return (
        <div className="misinfracciones-page">
            <div className="container">
                <h1 className="title">Mis Infracciones</h1>
                <div className="divider"></div>

                <Card className="info-card mb-3">
                    <p>Consulta tus folios y reimprime cuando sea necesario.</p>
                </Card>

                <Card className="p-3">
                    <Form.Control
                        type="text"
                        placeholder="Buscar por folio, conductor o fecha..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="mb-3"
                    />

                    {loading && <p className="mb-0">Cargando infracciones...</p>}
                    {!loading && error && <p className="text-danger mb-0">{error}</p>}

                    {!loading && !error && (
                        <div className="misinfracciones-table-wrap">
                            <Table striped bordered hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Conductor</th>
                                        <th>Fecha</th>
                                        <th>Motivo</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={5}>No hay infracciones para mostrar.</td>
                                        </tr>
                                    ) : (
                                        rows.map((inf) => (
                                            <tr key={inf.id}>
                                                <td>{inf.id}</td>
                                                <td>{`${inf.infractor_nombre || ""} ${inf.infractor_apellido_paterno || ""} ${inf.infractor_apellido_materno || ""}`.trim() || "Conductor Ausente"}</td>
                                                <td>{inf.fecha || "-"}</td>
                                                <td>{inf.motivo || "-"}</td>
                                                <td>
                                                    <Button size="sm" variant="dark" onClick={() => handleReimprimir(inf)}>
                                                        <PrinterFill /> Reimprimir
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
